import { z } from 'zod';
import { resolveSurface, validateSurfacePayload } from '@/screens/registry';
import { type PanelDescriptor } from '@/screens/types';
import { BASE_GRID_COLS, getWorkspaceGridCols } from '@/panels/grid';

export const LAYOUT_TEMPLATE_SURFACE = '__layout__';
const WORKSPACE_LAYOUT_VERSION = 2 as const;
const WORKSPACE_LAYOUT_SOURCE = 'workspace_snapshot' as const;

const LayoutGridSchema = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int(),
  h: z.number().int(),
});

const LayoutTileSchema = z.object({
  id: z.string().min(1),
  surface: z.string().min(1),
  payload: z.unknown(),
  grid: LayoutGridSchema,
});

export const LayoutConfigSchema = z.object({
  version: z.union([z.literal(1), z.literal(WORKSPACE_LAYOUT_VERSION)]),
  source: z.literal(WORKSPACE_LAYOUT_SOURCE).optional(),
  tiles: z.array(LayoutTileSchema),
});

export type LayoutConfig = z.infer<typeof LayoutConfigSchema>;
export type LayoutTemplateTile = z.infer<typeof LayoutTileSchema>;

export type HydratedLayout = {
  panels: PanelDescriptor[];
  droppedUnknownSurfaces: string[];
  droppedInvalidPayloads: string[];
};

function sanitizeGrid(grid: LayoutTemplateTile['grid']): PanelDescriptor['layout'] {
  const cols = getWorkspaceGridCols();
  const w = Math.max(1, Math.min(cols, Math.floor(grid.w)));
  const h = Math.max(1, Math.floor(grid.h));
  const x = Math.max(0, Math.min(cols - w, Math.floor(grid.x)));
  const y = Math.max(0, Math.floor(grid.y));
  return { x, y, w, h };
}

export function buildLayoutConfigFromPanels(panels: PanelDescriptor[]): LayoutConfig {
  return {
    version: WORKSPACE_LAYOUT_VERSION,
    source: WORKSPACE_LAYOUT_SOURCE,
    tiles: panels
      .filter((panel) => !panel.detached)
      .map((panel) => ({
        id: panel.panelId,
        surface: panel.surface,
        payload: panel.payload,
        grid: {
          x: panel.layout.x,
          y: panel.layout.y,
          w: panel.layout.w,
          h: panel.layout.h,
        },
      })),
  };
}

export function parseLayoutConfig(raw: unknown): LayoutConfig | null {
  if (typeof raw === 'string') {
    try {
      return parseLayoutConfig(JSON.parse(raw) as unknown);
    } catch {
      return null;
    }
  }

  const wrapped = LayoutConfigSchema.safeParse(raw);
  if (wrapped.success) {
    return wrapped.data;
  }

  const bareTiles = z.array(LayoutTileSchema).safeParse(raw);
  if (bareTiles.success) {
    return {
      version: 1,
      tiles: bareTiles.data,
    };
  }

  if (typeof raw !== 'object' || raw === null) {
    return null;
  }

  const maybeTiles = (raw as { tiles?: unknown }).tiles;
  const nestedTiles = z.array(LayoutTileSchema).safeParse(maybeTiles);
  if (!nestedTiles.success) {
    return null;
  }

  return {
    version: 1,
    tiles: nestedTiles.data,
  };
}

export function hydratePanelsFromLayoutConfig(
  config: LayoutConfig,
  now: number = Date.now(),
): HydratedLayout {
  const cols = getWorkspaceGridCols();
  const droppedUnknownSurfaces: string[] = [];
  const droppedInvalidPayloads: string[] = [];
  const trustSavedWorkspaceSnapshot =
    config.version === WORKSPACE_LAYOUT_VERSION || config.source === WORKSPACE_LAYOUT_SOURCE;
  const layoutCols = trustSavedWorkspaceSnapshot ? cols : BASE_GRID_COLS;

  const panels = config.tiles.flatMap((tile, index) => {
    if (tile.surface === LAYOUT_TEMPLATE_SURFACE) {
      droppedUnknownSurfaces.push(tile.surface);
      return [];
    }

    const entry = resolveSurface(tile.surface);
    if (!entry) {
      droppedUnknownSurfaces.push(tile.surface);
      return [];
    }

    if (!trustSavedWorkspaceSnapshot && !validateSurfacePayload(tile.surface, tile.payload)) {
      droppedInvalidPayloads.push(tile.surface);
      return [];
    }

    return [
      {
        panelId: tile.id,
        surface: tile.surface,
        kind: entry.kind,
        payload: tile.payload,
        layout: sanitizeGrid(tile.grid),
        layoutCols,
        detached: false,
        updatedAt: now + index,
      } satisfies PanelDescriptor,
    ];
  });

  return { panels, droppedUnknownSurfaces, droppedInvalidPayloads };
}
